type Child = Node | string | number | null | undefined | false | Child[];

type Handler<E extends Event = Event> = (ev: E) => void;

export interface Props {
  class?: string;
  text?: string;
  style?: Partial<CSSStyleDeclaration>;
  dataset?: Record<string, string>;
  attrs?: Record<string, string>;
  onclick?: Handler<MouseEvent>;
  oninput?: Handler<Event>;
  onchange?: Handler<Event>;
  onsubmit?: Handler<SubmitEvent>;
  onkeydown?: Handler<KeyboardEvent>;
  disabled?: boolean;
  type?: string;
  value?: string;
  placeholder?: string;
  title?: string;
  checked?: boolean;
  maxLength?: number;
  htmlFor?: string;
  id?: string;
  name?: string;
  autocomplete?: string;
  tabIndex?: number;
}

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [key, value] of Object.entries(props) as [keyof Props, unknown][]) {
      if (value === undefined) continue;
      switch (key) {
        case 'class':
          el.className = value as string;
          break;
        case 'text':
          el.textContent = value as string;
          break;
        case 'style':
          Object.assign(el.style, value);
          break;
        case 'dataset':
          Object.assign(el.dataset, value);
          break;
        case 'attrs':
          for (const [k, v] of Object.entries(value as Record<string, string>)) el.setAttribute(k, v);
          break;
        default:
          (el as unknown as Record<string, unknown>)[key] = value;
      }
    }
  }
  append(el, children);
  return el;
}

function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

export function replace(el: Element, ...children: Child[]): void {
  el.replaceChildren();
  append(el, children);
}
